const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeNameKey = (value) => String(value || 'item')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

const stableSerialize = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

const normalizeUsesState = (uses) => {
  if (!uses || typeof uses !== 'object') return null;
  const current = Number.isFinite(uses.current) ? uses.current : uses.cur;
  const max = Number.isFinite(uses.max) ? uses.max : null;
  return {
    current: Number.isFinite(current) ? current : null,
    max: Number.isFinite(max) ? max : null,
    resetType: String(uses.resetType || uses.reset || 'none'),
  };
};

const hasNonTrivialUsesState = (uses) => {
  const normalized = normalizeUsesState(uses);
  if (!normalized) return false;
  const current = normalized.current ?? normalized.max ?? 1;
  const max = normalized.max ?? 1;
  return max > 1 || current !== max || normalized.resetType !== 'none';
};

const hasMutableAbilityState = (abilities) => toArray(abilities).length > 0;

export const getLegacyItemBehaviorSignature = (item = {}) => stableSerialize({
  category: item.category || null,
  buffs: toArray(item.buffs),
  debuffs: toArray(item.debuffs),
  onUseEffects: toArray(item.onUseEffects),
  combatBonuses: toArray(item.combatBonuses),
  buffsApplyOnUse: item.buffsApplyOnUse === true,
  basePrice: Number.isFinite(item.basePrice) ? item.basePrice : null,
  tradeOnly: item.tradeOnly === true,
});

export const getLegacyItemProvenanceSignature = (item = {}) => stableSerialize({
  provenance: item.provenance || null,
  lootSource: item.lootSource || null,
});

const hasLegacyItemSourceProvenance = (item = {}) => Boolean(item?.provenance || item?.lootSource);

export const isLegacyInventoryItemStackSafe = (item = {}) => {
  if (!item || item.equipped === true) return false;
  if (hasNonTrivialUsesState(item.uses)) return false;
  if (hasMutableAbilityState(item.abilities)) return false;
  if (item.removedItemBuffs && Object.keys(item.removedItemBuffs).length > 0) return false;
  return true;
};

export const getLegacyItemStackKey = (item = {}) => {
  if (!isLegacyInventoryItemStackSafe(item)) return null;
  const behaviorSignature = getLegacyItemBehaviorSignature(item);
  if (item.itemId) return `definition:${item.itemId}:${behaviorSignature}`;
  return `legacy:${normalizeNameKey(item.name)}:${behaviorSignature}`;
};

export const canStackLegacyInventoryItems = (existingItem, incomingItem) => {
  const existingKey = getLegacyItemStackKey(existingItem);
  const incomingKey = getLegacyItemStackKey(incomingItem);
  if (!existingKey || !incomingKey || existingKey !== incomingKey) return false;
  const existingHasSourceProvenance = hasLegacyItemSourceProvenance(existingItem);
  const incomingHasSourceProvenance = hasLegacyItemSourceProvenance(incomingItem);
  if (
    (existingHasSourceProvenance || incomingHasSourceProvenance)
    && getLegacyItemProvenanceSignature(existingItem) !== getLegacyItemProvenanceSignature(incomingItem)
  ) {
    return false;
  }
  return true;
};

export const matchesLegacyInventoryItemRequirement = (
  item,
  {
    itemId = null,
    itemName = '',
    requireEquipped = false,
  } = {},
) => {
  if (!item) return false;
  const normalizedName = normalizeNameKey(itemName);
  const matchesDefinitionId = itemId && item.itemId === itemId;
  const matchesName = normalizedName && normalizeNameKey(item.name) === normalizedName;
  if (!matchesDefinitionId && !matchesName) return false;
  if (requireEquipped && !item.equipped) return false;
  return true;
};

export const hasLegacyInventoryItem = (inventory = [], requirement = {}) => (
  inventory.some((item) => matchesLegacyInventoryItemRequirement(item, requirement))
);

export const stackLegacyInventoryItem = (inventory = [], newItem = null) => {
  if (!newItem) return inventory;
  const existingIndex = inventory.findIndex((item) => canStackLegacyInventoryItems(item, newItem));
  if (existingIndex < 0) return [...inventory, newItem];
  return inventory.map((item, index) => (
    index === existingIndex
      ? { ...item, quantity: (item.quantity || 1) + (newItem.quantity || 1) }
      : item
  ));
};
